import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Users, Check, X, UserCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPilotApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('sub_pilots'); // sub_pilots | all
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [approvingBulk, setApprovingBulk] = useState(false);
  const adminEmail = typeof window !== 'undefined' ? (localStorage.getItem('userEmail') || 'info@trucknsendit.com') : 'info@trucknsendit.com';

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ admin_email: adminEmail });
      if (filter === 'sub_pilots') params.set('only_sub_pilots', 'true');
      const res = await axios.get(`${API}/admin/pilots/pending?${params.toString()}`);
      setRows(res.data || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load pending pilots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const handleApprove = async (row) => {
    try {
      await axios.post(`${API}/admin/pilots/${row.id}/approve?admin_email=${encodeURIComponent(adminEmail)}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (row) => {
    if (!window.confirm(`Reject ${row.name}?`)) return;
    try {
      await axios.post(`${API}/admin/pilots/${row.id}/reject?admin_email=${encodeURIComponent(adminEmail)}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to reject');
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map(r => r.id)));
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Approve ${selectedIds.size} pilot(s)?`)) return;
    setApprovingBulk(true);
    try {
      const res = await axios.post(
        `${API}/admin/pilots/bulk-approve?admin_email=${encodeURIComponent(adminEmail)}`,
        { pilot_registration_ids: Array.from(selectedIds) }
      );
      alert(res.data.message);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setApprovingBulk(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1e3a5f] flex items-center gap-2"><UserCheck className="w-6 h-6" /> Pilot Approval Queue</h1>
          <p className="text-sm text-gray-500">Approve or reject pending pilot registrations.</p>
        </div>

        <div className="flex gap-2 mb-4">
          {[
            { v: 'sub_pilots', label: 'Sub-pilots only', icon: Users },
            { v: 'all', label: 'All pending' },
          ].map(opt => {
            const Icon = opt.icon;
            return (
              <Button
                key={opt.v}
                variant={filter === opt.v ? 'default' : 'outline'}
                size="sm"
                className={filter === opt.v ? 'bg-[#1e3a5f]' : ''}
                onClick={() => setFilter(opt.v)}
                data-testid={`admin-pilot-approval-filter-${opt.v}`}
              >
                {Icon && <Icon className="w-3.5 h-3.5 mr-1.5" />}
                {opt.label}
              </Button>
            );
          })}
        </div>

        {loading ? (
          <p className="text-center py-10 text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <Card className="p-10 text-center">
            <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No pending pilot registrations.</p>
          </Card>
        ) : (
          <>
            {/* Bulk action bar */}
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.size === rows.length && rows.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4"
                  data-testid="approvals-select-all"
                />
                <span>{selectedIds.size === rows.length && rows.length > 0 ? 'Deselect all' : 'Select all'}</span>
              </label>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-lg px-3 py-2" data-testid="approvals-bulk-bar">
                  <span className="text-sm text-green-800"><strong>{selectedIds.size}</strong> selected</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedIds(new Set())}
                    className="text-gray-700 border-gray-300"
                    data-testid="approvals-clear"
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleBulkApprove}
                    disabled={approvingBulk}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="approvals-bulk-approve"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> {approvingBulk ? 'Approving…' : `Approve ${selectedIds.size}`}
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {rows.map(r => (
                <Card
                  key={r.id}
                  className={`p-4 border-l-4 border-l-yellow-500 ${selectedIds.has(r.id) ? 'ring-2 ring-green-400' : ''}`}
                  data-testid={`admin-pilot-pending-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <label className="flex items-start pt-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelected(r.id)}
                        className="w-4 h-4"
                        data-testid={`approvals-check-${r.id}`}
                      />
                    </label>
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.pilot_code && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#1e3a5f] text-white font-mono">
                          {r.pilot_code}
                        </span>
                      )}
                      <span className="font-semibold text-[#1e3a5f]">{r.name}</span>
                      <span className="text-[10px] bg-yellow-200 text-yellow-900 px-2 py-0.5 rounded uppercase font-semibold">Pending</span>
                      {r.agent_id && (
                        <span className="text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-semibold uppercase" data-testid={`sub-pilot-badge-${r.id}`}>
                          Sub-pilot
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{r.email} · {r.phone} {r.company_name && `· ${r.company_name}`}</p>
                    {r.agent_id && (
                      <p className="text-xs text-purple-700 mt-1">
                        Registered by <strong>{r.agent_name || '—'}</strong> ({r.agent_email_display || r.agent_email})
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(r)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`admin-approve-${r.id}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(r)}
                      className="text-red-600 border-red-400 hover:bg-red-50"
                      data-testid={`admin-reject-${r.id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
